// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/PlanVersion.yaml
// Generated: 2026-06-02T14:10:17.892Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { planVersionService } from '@/services/finance/PlanVersionService';
import { CreatePlanVersion, UpdatePlanVersion, PlanVersion } from '@/types/finance/PlanVersion';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for PlanVersion
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const planversionKeys = {
  all: (tenantId?: string | null) => ['planversions', tenantId] as const,
  lists: (tenantId?: string | null) => [...planversionKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...planversionKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...planversionKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...planversionKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all PlanVersions
 */
export function usePlanVersions(options?: Omit<UseQueryOptions<PlanVersion[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<PlanVersion[], Error>({
    queryKey: planversionKeys.lists(tenantId),
    queryFn: () => planVersionService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single PlanVersion by ID
 */
export function usePlanVersion(
  id: string,
  options?: Omit<UseQueryOptions<PlanVersion | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<PlanVersion | undefined, Error>({
    queryKey: planversionKeys.detail(id, tenantId),
    queryFn: () => planVersionService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new PlanVersion
 */
export function useCreatePlanVersion() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<PlanVersion, Error, CreatePlanVersion, { previousPlanVersions?: PlanVersion[] }>({
    mutationFn: (data: CreatePlanVersion) => planVersionService.create(data),
    onMutate: async (newPlanVersion) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planversionKeys.lists(tenantId) });

      // Snapshot previous value
      const previousPlanVersions = queryClient.getQueryData<PlanVersion[]>(planversionKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<PlanVersion[]>(planversionKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newPlanVersion, id: (newPlanVersion as { id?: string }).id ?? 'temp-' + Date.now() } as PlanVersion
      ]);

      return { previousPlanVersions };
    },
    onError: (_err, _newPlanVersion, context) => {
      // Rollback on error
      if (context?.previousPlanVersions) {
        queryClient.setQueryData(planversionKeys.lists(tenantId), context.previousPlanVersions);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planversionKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing PlanVersion
 */
export function useUpdatePlanVersion() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<PlanVersion, Error, { id: string; data: UpdatePlanVersion }, { previousPlanVersion?: PlanVersion; previousPlanVersions?: PlanVersion[] }>({
    mutationFn: ({ id, data }) => planVersionService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planversionKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: planversionKeys.lists(tenantId) });

      // Snapshot previous values
      const previousPlanVersion = queryClient.getQueryData<PlanVersion>(planversionKeys.detail(id, tenantId));
      const previousPlanVersions = queryClient.getQueryData<PlanVersion[]>(planversionKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<PlanVersion>(planversionKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as PlanVersion);

      // Optimistically update list
      queryClient.setQueryData<PlanVersion[]>(planversionKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as PlanVersion : item))
      );

      return { previousPlanVersion, previousPlanVersions };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousPlanVersion) {
        queryClient.setQueryData(planversionKeys.detail(id, tenantId), context.previousPlanVersion);
      }
      if (context?.previousPlanVersions) {
        queryClient.setQueryData(planversionKeys.lists(tenantId), context.previousPlanVersions);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planversionKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: planversionKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a PlanVersion
 */
export function useDeletePlanVersion() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousPlanVersions?: PlanVersion[] }>({
    mutationFn: (id: string) => planVersionService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planversionKeys.lists(tenantId) });

      // Snapshot previous value
      const previousPlanVersions = queryClient.getQueryData<PlanVersion[]>(planversionKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<PlanVersion[]>(planversionKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousPlanVersions };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousPlanVersions) {
        queryClient.setQueryData(planversionKeys.lists(tenantId), context.previousPlanVersions);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planversionKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSavePlanVersion() {
  const createMutation = useCreatePlanVersion();
  const updateMutation = useUpdatePlanVersion();

  return {
    mutate: (params: { id?: string; data: CreatePlanVersion | UpdatePlanVersion }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdatePlanVersion }, options);
      } else {
        return createMutation.mutate(params.data as CreatePlanVersion, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
